import {
  useGetTourQuery,
  useGetTourSearchLinksResultsQuery,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  LinkIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { Dropdown, DropdownItem, TextCaption, Tooltip } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  useAnalytics,
  useWorkspaceAnalytics,
} from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef, useState } from 'react';
import { useCopyUrl } from '../../../hooks/useCopyUrl';
import { CreateShareTourFlow } from '../../Share/ShareTourFlow';
import { usePermissionsContext } from '../../utils';
import { TourActionsDropdownContent } from './TourActionsDropdownContent';
import {
  CopyTourModalWithFolders,
  DuplicateTourModal,
  MoveTourWithFoldersModal,
} from './modals';
import { ArchiveTourModal } from './modals/ArchiveTourModal';
import type { TourActionsDropdownProps } from './types';

export const TourPageActionsDropdown = ({
  triggerElement,
  arrow,
  side,
  align,
  asChild,
  organizationId,
  toursShareAspectRatioEnabled,
  tourId,
  workspaceId,
  goToTour,
  goToTourPreview,
  handleEdit,
  goToCopiedTour,
  goToShares,
  goToWorkspacePage,
  goToFolderPage,
}: TourActionsDropdownProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const { getPermissions } = usePermissionsContext();

  const flags = useFlags();

  const { copyCurrentURL } = useCopyUrl();
  const archiveRef = useRef<HTMLDivElement>(null);
  const { data: tour } = useGetTourQuery({
    organizationId,
    workspaceId,
    id: tourId,
  });
  const { data: linksResponse } = useGetTourSearchLinksResultsQuery({
    workspaces: [workspaceId],
    organizationId,
    id: String(tourId),
    active: true,
  });

  const duplicateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.duplicateTour
  );
  const copyTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.copyTourToWorkspace
  );
  const moveTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.moveTourToWorkspace
  );
  const restoreTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.restoreATour
  );
  const updateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.updateTourById
  );

  const [
    showDuplicateTourModal,
    {
      setTrue: setTrueShowDuplicateTourModal,
      setFalse: setFalseShowDuplicateTourModal,
    },
  ] = useBoolean(false);
  const [
    showArchiveTourModal,
    {
      setTrue: setTrueShowArchiveTourModal,
      setFalse: setFalseShowArchiveTourModal,
    },
  ] = useBoolean(false);
  const { trackTourClickOpenCopyToWorkspaceModal } =
    useWorkspaceAnalytics('tour');
  const location = 'tour_info_page_actions_menu';

  const tourFolderId = String(tour?.folderId);

  return (
    <>
      <Dropdown
        width={200}
        asChild={asChild}
        open={isMenuOpen}
        triggerElement={triggerElement}
        onOpenChange={(open) => setIsMenuOpen(open)}
        {...{ arrow, side, align }}
      >
        <TourActionsDropdownContent
          previewTour={
            <DropdownItem
              iconLeft={<ShowOutlineIcon size={18} />}
              label="Preview"
              onClick={(event) => {
                event.preventDefault();
                setIsMenuOpen(false);
                trackEvent(AnalyticsTrackEventName.TOUR_CLICK_PREVIEW, {
                  location,
                });
                goToTourPreview(tourId);
              }}
            />
          }
          editTour={
            updateTourPermission && (
              <DropdownItem
                iconLeft={<EditIcon />}
                label="Edit"
                onClick={(event) => {
                  event.preventDefault();
                  handleEdit();
                }}
              />
            )
          }
          duplicateTour={
            duplicateTourPermission && (
              <DropdownItem
                iconLeft={<DuplicateOutlineIcon size={18} />}
                label="Duplicate tour"
                onClick={() => {
                  trackEvent(AnalyticsTrackEventName.TOUR_CLICK_DUPLICATE, {
                    location,
                  });
                  setTrueShowDuplicateTourModal();
                }}
              />
            )
          }
          copyTour={
            copyTourPermission && (
              <DropdownItem
                iconLeft={<CopyOutlineIcon size={18} />}
                label="Copy to..."
                onClick={() => {
                  trackEvent(
                    AnalyticsTrackEventName.TOUR_CLICK_COPY_TO_WORKSPACE,
                    {
                      location,
                    }
                  );
                  trackTourClickOpenCopyToWorkspaceModal();
                  NiceModal.show(CopyTourModalWithFolders, {
                    tour,
                    organizationId,
                  });
                }}
              />
            )
          }
          moveTour={
            moveTourPermission && (
              <DropdownItem
                iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                label="Move to..."
                onClick={() => {
                  trackEvent(
                    AnalyticsTrackEventName.TOUR_CLICK_MOVE_TO_WORKSPACE,
                    {
                      location,
                    }
                  );
                  NiceModal.show(MoveTourWithFoldersModal, {
                    tour,
                    organizationId,
                    workspaceId: tour?.workspaceId,
                    onSuccess: goToCopiedTour,
                  });
                }}
              />
            )
          }
          shareTour={
            <DropdownItem
              iconLeft={<ShareOutlineIcon />}
              label="Share"
              onClick={() => {
                NiceModal.show(CreateShareTourFlow, {
                  workspaceId,
                  toursShareAspectRatioEnabled: !!toursShareAspectRatioEnabled,
                  organizationId,
                  tourId,
                  goToShares: () => goToShares(tourId),
                });
              }}
            />
          }
          copyUrl={
            flags.copyUrl && (
              <DropdownItem
                iconLeft={<LinkIcon size={18} />}
                label="Copy page URL"
                onClick={() => {
                  copyCurrentURL();
                }}
              />
            )
          }
          archiveTour={
            restoreTourPermission && (
              <>
                <div ref={archiveRef}>
                  <DropdownItem
                    disabled={!!linksResponse?.metadata?.totalItems}
                    iconLeft={<ArchiveIcon size={18} />}
                    label="Archive"
                    onClick={() => {
                      trackEvent(AnalyticsTrackEventName.TOUR_CLICK_ARCHIVE, {
                        location,
                      });
                      setTrueShowArchiveTourModal();
                    }}
                  />
                </div>
                {!!linksResponse?.metadata?.totalItems && (
                  <Tooltip
                    anchorRef={archiveRef}
                    placement="bottom"
                    offset={[0, 24]}
                  >
                    <div className="p-2.5">
                      <TextCaption
                        label="To archive this tour, please deactivate all of its shared links first"
                        color="text-ds-natural-600"
                      />
                    </div>
                  </Tooltip>
                )}
              </>
            )
          }
        />
      </Dropdown>
      {showDuplicateTourModal && (
        <DuplicateTourModal
          onClose={setFalseShowDuplicateTourModal}
          show={showDuplicateTourModal}
          organizationId={organizationId}
          goToDuplicatedTour={goToTour}
          tourId={tourId}
        />
      )}
      {showArchiveTourModal && (
        <ArchiveTourModal
          folderId={tourFolderId}
          tourId={tourId}
          organizationId={Number(organizationId)}
          show={showArchiveTourModal}
          onClose={setFalseShowArchiveTourModal}
          navigateOnSuccess={() =>
            tourFolderId === '0'
              ? goToWorkspacePage(workspaceId)
              : goToFolderPage(tourFolderId, workspaceId)
          }
        />
      )}
    </>
  );
};
